import { useId } from "react"
import { useTranslation } from "react-i18next"
import { FormError } from "@/components/ui/form"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

export type SelectUI = { value: string; label: string }

interface CustomSelectProps extends Omit<
  React.ComponentPropsWithoutRef<"select">,
  "onSelect" | "value" | "defaultValue"
> {
  label: string
  options: ReadonlyArray<SelectUI>
  onSelect?(value: string): void
  error?: string
  value: string | null
  testId: string
  disabled?: boolean
  showValueLabel?: boolean
  contentClassName?: string
  useTranslatedValue?: boolean
  renderOption?: (option: SelectUI, index: number) => React.ReactNode
  ref?: React.Ref<HTMLButtonElement>
}

export const CustomSelect = (props: CustomSelectProps) => {
  const {
    label,
    onSelect,
    options,
    className,
    error,
    disabled,
    showValueLabel,
    contentClassName,
    useTranslatedValue,
    renderOption,
    ref,
    testId,
  } = props
  const { t } = useTranslation()
  const selectId = useId()
  const value = props.value ?? ""

  const valueLabel = options.find((v) => v.value === value)?.label ?? ""
  const shownValueLabel = useTranslatedValue ? t(valueLabel) : valueLabel
  return (
    <Select value={value} onValueChange={onSelect} disabled={disabled}>
      <div className={cn(className)} data-testid={testId}>
        <SelectTrigger ref={ref} id={selectId} hasError={!!error}>
          <SelectValue placeholder={label}>
            <LabelAndSelectValue label={label} value={showValueLabel ? shownValueLabel : value} />
          </SelectValue>
        </SelectTrigger>
        {error && <FormError>{error}</FormError>}
      </div>
      <SelectContent className={contentClassName}>
        {options.map((option, idx) => {
          if (renderOption) return renderOption(option, idx)
          const testId = `${option.value.toLowerCase()}-dropdown-item`
          return (
            <SelectItem key={option.value} value={option.value} data-testid={testId}>
              {t(option.label)}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}
